import React, {useEffect, useState} from 'react';
import {Box, CircularProgress, Snackbar, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {Outlet, useNavigate} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {emitOnce} from '@ngneat/elf';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';

import {Roles, searchUsersEffect, User, usersQuery, usersService, UserType} from '@store/settings/users';

import {Colors} from '@constants/colors.constants';

import SearchComponent from '@components/inputs/search/Search.component';
import NewTableComponent from '@components/table/NewTable.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {Add, Refresh} from '@mui/icons-material';
import {finalize} from 'rxjs';
import {Download} from '@icons/index';
import ExpandableIconButtonComponent from '@components/buttons/ExpandableIconButton.component';
import SelectFileButton from '@components/inputs/selectFileButton/SelectFileButton.component';

const UsersTab = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const searchUsers = useEffectFn(searchUsersEffect);

  const [{users, loading}] = useObservable(usersQuery.users$);

  const [CSVLoading, setCSVLoading] = useState(false);

  const [filters] = useObservable(usersQuery.filters$);
  const [sortModel] = useObservable(usersQuery.sort$);
  const [tablePagination] = useObservable(usersQuery.pagination$);

  const [importLoading, setImportLoading] = useState(false);

  useEffect(() => {
    searchUsers({filters, tablePagination, sort: sortModel});
  }, [filters, searchUsers, sortModel, tablePagination]);

  const handleImportUsers = (file: File) => {
    setImportLoading(true);
    usersService.importUsersFromCsv(file).subscribe({
      next: (data) => {
        setImportLoading(false);
        enqueueSnackbar(t('settings.users.import.success', {
          updatedCount: data.updatedCount,
          insertedCount: data.insertedCount
        }), {variant: 'success'});
      },
      error: (err) => {
        setImportLoading(false);
        enqueueSnackbar(err.text, err.options);
      },
      complete: () => {
        setImportLoading(false);
      }
    });
  };

  const handleRefreshUsers = () => {
    setImportLoading(true);
    usersService.refreshUsers().subscribe({
      next: (data) => {
        setImportLoading(false);
        enqueueSnackbar(t('settings.users.refresh.success', {
          deletedCount: data.deletedCount,
          insertedCount: data.insertedCount
        }), {variant: 'success'});
      },
      error: (err) => {
        setImportLoading(false);
        enqueueSnackbar(err.text, err.options);
      },
      complete: () => {
        setImportLoading(false);
      }
    });
  };

  const handleExportCSV = () => {
    setCSVLoading(true);
    usersService.getUsersCSV()
      .pipe(finalize(() => setCSVLoading(false)))
      .subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  return (
    <>
      <Stack flex={1} width="100%" spacing={2} p="25px">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontSize="30px" fontWeight="700">
            {t('routes.users')}
          </Typography>
          <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
            <ExpandableIconButtonComponent disabled={CSVLoading} onClick={handleExportCSV} variant="outlined"
                                           color="primary" startIcon={<Download/>} title={t('global.exportCSV')}/>
            <ExpandableIconButtonComponent startIcon={<Refresh/>} variant="contained" color="primary"
                                           onClick={() => handleRefreshUsers()}
                                           title={t('settings.users.refresh.button')}/>
            <SelectFileButton
              title={t('settings.users.import.button')}
              handleFileSelected={handleImportUsers}
              accept="text/csv"/>
            <ExpandableIconButtonComponent startIcon={<Add/>} variant="contained" color="primary"
                                           title={t('settings.users.create.button')}
                                           onClick={() => navigate('new')}/>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
          <Box width="500px">
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  usersService.setFilters('search')(value);
                  usersService.setPagination('page')(1);
                });
              }}/>
          </Box>
          <Stack direction="column">
            <TableFiltersComponent
              filters={filters}
              handleSave={usersService.setFilters('tableFilters')}
              handleReset={() => usersService.resetFilters()}/>
          </Stack>
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT_BOLD({
              field: 'lastName',
              label: t('settings.users.table.name'),
              valueGetter: (params: GridValueGetterParams<any, User>) => `${params.row.firstName} ${params.row.lastName}`,
              fullWidth: true,
              minWidth: 350
            }),
            COLUMN_TYPES.TEXT({
              field: 'email',
              label: t('settings.users.table.email'),
              fullWidth: true,
              minWidth: 300
            }),
            COLUMN_TYPES.TEXT({
              field: 'enterprise',
              label: t('settings.users.table.enterprise'),
              valueGetter: (params: GridValueGetterParams<any, User>) => {
                const total = params.row.clients.length + params.row.suppliers.length;

                if (total === 0)
                  return '-';
                if (total > 1)
                  return `multiple (${total})`;
                return params.row.clients.length === 1 ? params.row.clients[0].name : params.row.suppliers[0].name;
              },
              fullWidth: true,
              minWidth: 350
            }),
            COLUMN_TYPES.TEXT({
              field: 'kind',
              label: t('settings.users.table.type'),
              valueFormatter: (params) =>
                UserType.selectItems().find((s) => s.value === params.value)?.label || '',
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'roles',
              label: t('settings.users.table.role'),
              valueGetter: (params: GridValueGetterParams<any, User>) =>
                Roles.selectItems().find((s) => s.value === params.value?.[0])?.label || '',
              minWidth: 120,
            }),
            COLUMN_TYPES.TEXT({
              field: 'lastActivityAt',
              label: t('settings.users.table.lastActivityAt'),
              valueFormatter: (params) => params.value ? t('global.dateAndHour', {date: new Date(params.value)}) : '',
              width: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'createdAt',
              label: t('settings.users.table.createdDate'),
              valueFormatter: (params) => params.value ? t('global.date', {date: new Date(params.value)}) : '',
              width: 130,
              fullWidth: true,
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (user: User) => navigate(`${user.id}`),
            }),
          ]}
          handlePageSizeChange={(value) => {
            usersService.setPagination('limit')(value);
            usersService.setPagination('page')(1);
          }}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => usersService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={usersService.setSort}
          sortModel={[sortModel]}
          disableRowSelection
          totalRows={usersQuery.totalRows}
          data={users}/>
      </Stack>
      <Outlet/>
      <Snackbar open={importLoading} anchorOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }} style={{backgroundColor: Colors.text}}>
        <Stack p={1} direction="row" alignItems="center">
          <CircularProgress size={15}/>
          <Typography color="primary" ml={1}>{t('settings.users.import.loading')}</Typography>
        </Stack>

      </Snackbar>
    </>
  );
};

export default UsersTab;
